import storage from "../utils/storage.js";

type SpeedtestMode = "quick" | "saver" | "full";

interface ModeProfile {
  label: string;
  hint: string;
  timeDl: number;
  timeUl: number;
  countPing: number;
  downloadStreams: number;
  uploadStreams: number;
  expectedDlRange: [number, number];
  expectedUlRange: [number, number];
  maxTotalSeconds: number;
}

interface SpeedtestLiveData {
  dlStatus?: string;
  ulStatus?: string;
  pingStatus?: string;
  jitterStatus?: string;
  testState?: number;
}

interface SpeedtestResultPayload {
  mode: SpeedtestMode;
  dlMbps: number;
  ulMbps: number;
  pingMs: number;
  jitterMs: number;
}

interface SpeedtestInstance {
  onupdate: ((data: SpeedtestLiveData) => void) | null;
  onend: ((aborted: boolean) => void) | null;
  setParameter: (name: string, value: string | number | boolean) => void;
  start: () => void;
  abort: () => void;
}

type SpeedtestConstructor = new () => SpeedtestInstance;

declare global {
  interface Window {
    Speedtest?: SpeedtestConstructor;
  }
}

const MODE_STORAGE_KEY = "speedtest.mode";

const MODE_PROFILES: Record<SpeedtestMode, ModeProfile> = {
  quick: {
    label: "Quick",
    hint: "Fast estimate with the lowest data usage.",
    timeDl: 2,
    timeUl: 1,
    countPing: 6,
    downloadStreams: 1,
    uploadStreams: 1,
    expectedDlRange: [80, 200],
    expectedUlRange: [10, 40],
    maxTotalSeconds: 7,
  },
  saver: {
    label: "Saver",
    hint: "Balanced mode for reliable daily checks.",
    timeDl: 4,
    timeUl: 3,
    countPing: 8,
    downloadStreams: 2,
    uploadStreams: 1,
    expectedDlRange: [150, 350],
    expectedUlRange: [20, 80],
    maxTotalSeconds: 12,
  },
  full: {
    label: "Full",
    hint: "Longest run for highest consistency and confidence.",
    timeDl: 7,
    timeUl: 6,
    countPing: 10,
    downloadStreams: 3,
    uploadStreams: 2,
    expectedDlRange: [300, 1000],
    expectedUlRange: [30, 120],
    maxTotalSeconds: 20,
  },
};

function asNumber(value?: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMetric(value: number, unit: string): string {
  if (value <= 0) {
    return "-";
  }
  return `${value.toFixed(2)} ${unit}`;
}

function estimateDataMb(
  dlMbps: number,
  ulMbps: number,
  profile: ModeProfile,
  runDownload: boolean,
  runUpload: boolean,
): number {
  const dlMb = runDownload ? dlMbps * profile.timeDl : 0;
  const ulMb = runUpload ? ulMbps * profile.timeUl : 0;
  return (dlMb + ulMb) / 8;
}

function formatDataRange(
  profile: ModeProfile,
  runDownload: boolean,
  runUpload: boolean,
): string {
  const minMb = estimateDataMb(
    profile.expectedDlRange[0],
    profile.expectedUlRange[0],
    profile,
    runDownload,
    runUpload,
  );
  const maxMb = estimateDataMb(
    profile.expectedDlRange[1],
    profile.expectedUlRange[1],
    profile,
    runDownload,
    runUpload,
  );

  return `${Math.round(minMb)}-${Math.round(maxMb)} MB`;
}

function isLocalRuntime(): boolean {
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]"
  );
}

function getSavedMode(): SpeedtestMode {
  const saved = storage.get<string>(MODE_STORAGE_KEY, "saver");
  if (saved === "quick" || saved === "full" || saved === "saver") {
    return saved;
  }
  return "saver";
}

function setModeButtonState(
  buttons: NodeListOf<HTMLButtonElement>,
  activeMode: SpeedtestMode,
): void {
  buttons.forEach((button) => {
    const isActive = button.dataset.mode === activeMode;
    button.classList.toggle("border-brand-primary", isActive);
    button.classList.toggle("bg-rose-50", isActive);
    button.classList.toggle("text-brand-primary", isActive);
    button.classList.toggle("border-slate-200", !isActive);
  });
}

function sendResultLog(
  endpoint: string,
  payload: SpeedtestResultPayload,
): void {
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(endpoint, blob);
    return;
  }

  fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Ignore telemetry errors in UI.
  });
}

function initSpeedtest(): void {
  const app = document.getElementById("speedtest-app");
  if (!app) {
    return;
  }

  const speedtestPath = app.dataset.speedtestPath || "/speedtest/";
  const logEndpoint = app.dataset.logEndpoint || "";
  const estimateEl = document.getElementById("speedtest-estimate");
  const statusEl = document.getElementById("speedtest-status");
  const startButton = document.getElementById(
    "speedtest-start",
  ) as HTMLButtonElement | null;
  const stopButton = document.getElementById(
    "speedtest-stop",
  ) as HTMLButtonElement | null;
  const dlEl = document.getElementById("speedtest-dl");
  const ulEl = document.getElementById("speedtest-ul");
  const pingEl = document.getElementById("speedtest-ping");
  const jitterEl = document.getElementById("speedtest-jitter");
  const dlCard = document.getElementById("speedtest-dl-card");
  const ulCard = document.getElementById("speedtest-ul-card");
  const enableDownloadInput = document.getElementById(
    "speedtest-enable-download",
  ) as HTMLInputElement | null;
  const enableUploadInput = document.getElementById(
    "speedtest-enable-upload",
  ) as HTMLInputElement | null;
  const optionsErrorEl = document.getElementById("speedtest-options-error");
  const modeButtons = document.querySelectorAll<HTMLButtonElement>(
    ".speedtest-mode-btn",
  );

  if (
    !estimateEl ||
    !statusEl ||
    !startButton ||
    !stopButton ||
    !dlEl ||
    !ulEl ||
    !pingEl ||
    !jitterEl ||
    !dlCard ||
    !ulCard ||
    !enableDownloadInput ||
    !enableUploadInput ||
    !optionsErrorEl
  ) {
    return;
  }

  let selectedMode: SpeedtestMode = getSavedMode();
  let isRunning = false;
  let runDownload = true;
  let runUpload = true;
  let hardStopTimer: number | null = null;
  let currentResult: SpeedtestResultPayload = {
    mode: selectedMode,
    dlMbps: 0,
    ulMbps: 0,
    pingMs: 0,
    jitterMs: 0,
  };

  const applyModeUi = (mode: SpeedtestMode): void => {
    const profile = MODE_PROFILES[mode];
    selectedMode = mode;
    currentResult.mode = mode;
    estimateEl.textContent = `Estimated data use: ${formatDataRange(profile, runDownload, runUpload)} (${runDownload ? `${profile.timeDl}s download` : "download off"} + ${runUpload ? `${profile.timeUl}s upload` : "upload off"}, max ${profile.maxTotalSeconds}s). ${profile.hint}`;
    setModeButtonState(modeButtons, selectedMode);
    storage.set(MODE_STORAGE_KEY, selectedMode);
  };

  const updateOptionUi = (): void => {
    runDownload = enableDownloadInput.checked;
    runUpload = enableUploadInput.checked;
    const invalid = !runDownload && !runUpload;
    optionsErrorEl.classList.toggle("hidden", !invalid);
    startButton.disabled = invalid;
    startButton.classList.toggle("opacity-60", invalid);
    startButton.classList.toggle("cursor-not-allowed", invalid);
    dlCard.classList.toggle("opacity-50", !runDownload);
    ulCard.classList.toggle("opacity-50", !runUpload);

    if (!runDownload) {
      dlEl.textContent = "Off";
    } else if (!isRunning && dlEl.textContent === "Off") {
      dlEl.textContent = "-";
    }

    if (!runUpload) {
      ulEl.textContent = "Off";
    } else if (!isRunning && ulEl.textContent === "Off") {
      ulEl.textContent = "-";
    }

    applyModeUi(selectedMode);
  };

  const toggleControlsDisabled = (disabled: boolean): void => {
    enableDownloadInput.disabled = disabled;
    enableUploadInput.disabled = disabled;
    modeButtons.forEach((button) => {
      button.disabled = disabled;
      button.classList.toggle("opacity-70", disabled);
      button.classList.toggle("cursor-not-allowed", disabled);
    });
  };

  const clearHardStopTimer = (): void => {
    if (hardStopTimer !== null) {
      window.clearTimeout(hardStopTimer);
      hardStopTimer = null;
    }
  };

  applyModeUi(selectedMode);
  updateOptionUi();

  if (isLocalRuntime()) {
    statusEl.textContent =
      "Localhost detected. Results can be much higher than your ISP plan because this setup tests local routing/server path. Use your public domain or another device on an external network for real internet speed.";
  }

  modeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (isRunning) {
        return;
      }
      const mode = button.dataset.mode;
      if (mode === "quick" || mode === "saver" || mode === "full") {
        applyModeUi(mode);
      }
    });
  });

  enableDownloadInput.addEventListener("change", updateOptionUi);
  enableUploadInput.addEventListener("change", updateOptionUi);

  startButton.addEventListener("click", () => {
    if (!runDownload && !runUpload) {
      return;
    }

    const Speedtest = window.Speedtest;
    if (!Speedtest) {
      statusEl.textContent =
        "Speedtest engine could not be loaded. Please refresh this page.";
      return;
    }

    const profile = MODE_PROFILES[selectedMode];
    const test = new Speedtest();
    test.setParameter("url_dl", `${speedtestPath}backend/garbage.php`);
    test.setParameter("url_ul", `${speedtestPath}backend/empty.php`);
    test.setParameter("url_ping", `${speedtestPath}backend/empty.php`);
    test.setParameter("url_getIp", `${speedtestPath}backend/getIP.php`);
    const testOrder = ["I", "P"];
    if (runDownload) {
      testOrder.push("D");
    }
    if (runUpload) {
      testOrder.push("U");
    }
    test.setParameter("test_order", testOrder.join("_"));
    test.setParameter("time_auto", false);
    test.setParameter("time_dl_max", runDownload ? profile.timeDl : 0);
    test.setParameter("time_ul_max", runUpload ? profile.timeUl : 0);
    // Keep grace windows small so configured durations feel predictable.
    test.setParameter("time_dlGraceTime", 0.5);
    test.setParameter("time_ulGraceTime", 1);
    test.setParameter("count_ping", profile.countPing);
    test.setParameter("xhr_dlMultistream", profile.downloadStreams);
    test.setParameter("xhr_ulMultistream", profile.uploadStreams);

    isRunning = true;
    toggleControlsDisabled(true);
    startButton.classList.add("hidden");
    stopButton.classList.remove("hidden");
    statusEl.textContent = `Running ${profile.label} mode...`;
    dlEl.textContent = "-";
    ulEl.textContent = "-";
    pingEl.textContent = "-";
    jitterEl.textContent = "-";

    clearHardStopTimer();
    hardStopTimer = window.setTimeout(() => {
      if (!isRunning) {
        return;
      }
      test.abort();
      statusEl.textContent = `Stopped after ${profile.maxTotalSeconds}s limit for ${profile.label} mode.`;
    }, profile.maxTotalSeconds * 1000);

    stopButton.onclick = () => {
      clearHardStopTimer();
      test.abort();
      statusEl.textContent = "Test cancelled.";
    };

    test.onupdate = (data) => {
      const dl = asNumber(data.dlStatus);
      const ul = asNumber(data.ulStatus);
      const ping = asNumber(data.pingStatus);
      const jitter = asNumber(data.jitterStatus);

      currentResult = {
        mode: selectedMode,
        dlMbps: runDownload ? dl : 0,
        ulMbps: runUpload ? ul : 0,
        pingMs: ping,
        jitterMs: jitter,
      };

      dlEl.textContent = runDownload ? formatMetric(dl, "Mbps") : "Off";
      ulEl.textContent = runUpload ? formatMetric(ul, "Mbps") : "Off";
      pingEl.textContent = formatMetric(ping, "ms");
      jitterEl.textContent = formatMetric(jitter, "ms");
    };

    test.onend = (aborted) => {
      isRunning = false;
      clearHardStopTimer();
      toggleControlsDisabled(false);
      startButton.classList.remove("hidden");
      stopButton.classList.add("hidden");
      stopButton.onclick = null;
      updateOptionUi();

      if (aborted) {
        return;
      }

      const actualUsedMb = estimateDataMb(
        currentResult.dlMbps,
        currentResult.ulMbps,
        profile,
        runDownload,
        runUpload,
      );
      statusEl.textContent = `Completed in ${profile.label} mode. Approx data used: ${actualUsedMb.toFixed(0)} MB. Run twice and compare averages for best consistency.`;
      if (logEndpoint) {
        sendResultLog(logEndpoint, currentResult);
      }
    };

    test.start();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSpeedtest);
} else {
  initSpeedtest();
}
